import React, { useState } from 'react';
import { Avatar, Tooltip } from '@mui/material';
import { X } from 'lucide-react';
import { DeviceAccountCard } from '../utils/deviceAccounts';
import { getSessionToken } from '../utils/sessionManager';

interface Props {
    accounts: DeviceAccountCard[];
    onSelect: (account: DeviceAccountCard) => void;
    onRemove: (account: DeviceAccountCard) => Promise<void>;
    onChangeAccount: () => void;
}

const INTERFACE_LABELS: Record<string, string> = {
    admin: 'Админ-панель',
    dashboard: 'Кабинет аптеки'
};

// Роль без цвета бейджа: нейтральный серый
const NEUTRAL_BADGE = { bg: '#374151', text: '#d1d5db' };

const accountSubtitle = (account: DeviceAccountCard): string => {
    return [
        account.fullName ? account.username : '',
        account.targetInterface === 'dashboard' && account.pharmacyNum ? `Аптека №${account.pharmacyNum}` : ''
    ].filter(Boolean).join(' · ');
};

/**
 * Экран входа с аккаунтами устройства и «Сменить аккаунт».
 * Рамка карточки и кольцо аватара - цвет текста бейджа роли (badge.text), справа - куда ведет аккаунт.
 * Живая сессия входит по клику; из аккаунта «Вы вышли» карточка бледная, клик открывает форму с его логином.
 * Крестик удаляет карточку с устройства после подтверждения прямо в строке.
 */
const ActiveAccountsPanel: React.FC<Props> = ({ accounts, onSelect, onRemove, onChangeAccount }) => {
    const currentToken = getSessionToken();
    const [confirmingId, setConfirmingId] = useState<number | null>(null);
    const [removingId, setRemovingId] = useState<number | null>(null);

    const confirmRemove = async (account: DeviceAccountCard) => {
        setRemovingId(account.userId);
        try {
            await onRemove(account);
        } finally {
            setRemovingId(null);
            setConfirmingId(null);
        }
    };

    return (
        <>
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold tracking-tight mb-2 text-white">Добро пожаловать!</h1>
                <p className="text-gray-400 text-sm">Выберите аккаунт, чтобы продолжить</p>
            </div>

            <div className="space-y-2.5 max-h-[60vh] overflow-y-auto p-1">
                {accounts.map((account) => {
                    const badge = account.badge || NEUTRAL_BADGE;
                    const name = account.fullName || account.username;

                    if (confirmingId === account.userId) {
                        const isRemoving = removingId === account.userId;
                        return (
                            <div
                                key={account.userId}
                                className="flex min-h-[70px] items-center gap-3 rounded-xl border border-gray-700 bg-gray-900 px-4 py-3"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium text-white">Удалить {name} с устройства?</div>
                                    <div className="truncate text-xs text-gray-400">
                                        {account.signedOut ? 'Карточка исчезнет с экрана входа' : 'Сессия будет закрыта, карточка исчезнет'}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setConfirmingId(null)}
                                    disabled={isRemoving}
                                    className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 transition-colors hover:bg-gray-800 hover:text-white disabled:opacity-60 cursor-pointer"
                                >
                                    Отмена
                                </button>
                                <button
                                    type="button"
                                    onClick={() => confirmRemove(account)}
                                    disabled={isRemoving}
                                    className="shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-500 disabled:opacity-60 cursor-pointer"
                                >
                                    {isRemoving ? 'Удаление...' : 'Удалить'}
                                </button>
                            </div>
                        );
                    }

                    const subtitle = accountSubtitle(account);
                    const interfaceLabel = INTERFACE_LABELS[account.targetInterface];
                    const isCurrent = !account.signedOut && account.sessionToken === currentToken;

                    return (
                        <div
                            key={account.userId}
                            style={{ borderColor: badge.text, '--account-border': badge.text } as React.CSSProperties}
                            className={`flex items-stretch rounded-xl border bg-gray-900 transition-[opacity,box-shadow] hover:shadow-[0_0_0_1px_var(--account-border)] ${account.signedOut ? 'opacity-55 hover:opacity-85' : ''}`}
                        >
                            <button
                                type="button"
                                onClick={() => onSelect(account)}
                                className="flex min-w-0 flex-1 items-center gap-3.5 rounded-l-xl px-4 py-3 text-left transition-colors hover:bg-gray-800 focus:outline-none focus-visible:bg-gray-800 cursor-pointer"
                            >
                                <Avatar
                                    src={account.avatar || undefined}
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        bgcolor: badge.bg,
                                        color: badge.text,
                                        fontSize: 16,
                                        fontWeight: 'bold',
                                        outline: `2px solid ${badge.text}`,
                                        outlineOffset: 2
                                    }}
                                >
                                    {(account.fullName?.[0] || account.username?.[0] || 'U').toUpperCase()}
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium text-white">{name}</div>
                                    {subtitle && <div className="truncate text-xs text-gray-400">{subtitle}</div>}
                                </div>
                                <div className="shrink-0 text-right text-xs">
                                    {interfaceLabel && <div className="text-gray-400">{interfaceLabel}</div>}
                                    {account.signedOut && <div className="mt-0.5 text-gray-300">Вы вышли</div>}
                                    {isCurrent && (
                                        <div className="mt-0.5 flex items-center justify-end gap-1.5 text-emerald-400">
                                            <span className="size-1.5 rounded-full bg-emerald-400" />
                                            Текущий
                                        </div>
                                    )}
                                </div>
                            </button>
                            <Tooltip title="Удалить с устройства" arrow placement="top">
                                <button
                                    type="button"
                                    onClick={() => setConfirmingId(account.userId)}
                                    aria-label="Удалить с устройства"
                                    className="flex w-10 shrink-0 items-center justify-center rounded-r-xl text-gray-500 transition-colors hover:bg-gray-800 hover:text-white focus:outline-none focus-visible:bg-gray-800 focus-visible:text-white cursor-pointer"
                                >
                                    <X className="size-4" />
                                </button>
                            </Tooltip>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    type="button"
                    onClick={onChangeAccount}
                    className="text-sm text-gray-400 hover:text-white transition-colors underline decoration-dotted cursor-pointer"
                >
                    Сменить аккаунт
                </button>
            </div>
        </>
    );
};

export default ActiveAccountsPanel;
